import { Outlet, useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import {
    faMapLocationDot,
    faSitemap,
    faChess,
    faBell,
    faBoxArchive,
    faWalkieTalkie,
} from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../store';
import { fetchEvent } from '../../store/eventsSlice';
import { fetchFactions } from '../../store/orbatSlice';
import eventService from '../../services/eventService';
import FactionSwitcher from '../menu/FactionSwitcher';
import Logo from '../common/Logo';
import { SidebarMenu, SidebarMenuItem } from '../common/SidebarMenu';
import { UserMenu } from '../common/UserMenu';

export default function EventLayout() {
    const { eventId } = useParams<{ eventId: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { currentEvent, isLoading, error } = useSelector((state: RootState) => state.events);
    const userRole = useSelector((state: RootState) => state.auth.user?.role);
    const isGamemasterOrAdmin = userRole === 'gamemaster' || userRole === 'admin';

    // Fetch event details when eventId changes
    useEffect(() => {
        if (eventId) {
            // If we don't have this event loaded, fetch it
            if (!currentEvent || currentEvent.id !== eventId) {
                dispatch(fetchEvent(eventId));
            }
            // Store as last active event
            eventService.setLastEventId(eventId);
        }
    }, [eventId, currentEvent, dispatch]);

    // Load factions when event context is available
    useEffect(() => {
        if (eventId) {
            dispatch(fetchFactions(eventId));
        }
    }, [eventId, dispatch]);

    const handleSelectEvent = () => {
        navigate('/dashboard');
    };

    // Menu entries for this event; Clients is gated on gamemaster/admin role.
    const menuItems: SidebarMenuItem[] = useMemo(() => {
        const base = `/events/${eventId}`;
        const items: SidebarMenuItem[] = [
            { to: `${base}/map`, icon: faMapLocationDot, label: 'Map', end: true },
            { to: `${base}/orbat`, icon: faSitemap, label: 'ORBAT', end: true },
            // { to: `${base}/settings`, icon: faChess, label: 'Game settings', end: true },
            // { to: `${base}/alarms`, icon: faBell, label: 'Alarms', end: true },
            { to: `${base}/map-bundles`, icon: faBoxArchive, label: 'Map Bundles', end: true },
        ];
        if (isGamemasterOrAdmin) {
            items.push({ to: `${base}/clients`, icon: faWalkieTalkie, label: 'Clients', end: true });
        }
        return items;
    }, [eventId, isGamemasterOrAdmin]);

    // Show loading while fetching event
    if (isLoading && !currentEvent) {
        return (
            <div className="d-flex h-100 align-items-center justify-content-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading event...</span>
                </Spinner>
            </div>
        );
    }

    // Show error if event not found
    if (error && !currentEvent) {
        return (
            <div className="d-flex h-100 align-items-center justify-content-center">
                <div className="text-center">
                    <h4 className="text-danger">Event Not Found</h4>
                    <p className="text-muted">{error}</p>
                    <button className="btn btn-primary" onClick={handleSelectEvent}>
                        Back to Events
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex h-100">
            <div className="col-2 h-100 p-0 d-flex flex-column bg-brand-surface border-end">
                <div className="p-3 text-center border-bottom">
                    <Logo variant="icon" maxHeight={64} className="mb-2" />
                    <h2 className="h2 mb-0">{currentEvent?.name || 'Loading...'}</h2>
                </div>
                <SidebarMenu items={menuItems} />

                {/* Faction switcher */}
                <div className="mt-auto p-2 border-top">
                    <FactionSwitcher />
                </div>

                {/* User menu at the bottom of sidebar */}
                <div className="p-2 border-top">
                    <UserMenu onSelectEvent={handleSelectEvent} />
                </div>
            </div>
            <div className="col-10 h-100 p-0">
                <Outlet />
            </div>
        </div>
    );
}
